import type { EmbedProvider } from './types';

export const KNOWN_PROVIDERS: ReadonlySet<EmbedProvider> =
  new Set<EmbedProvider>([
    'youtube',
    'everviz',
    'twitter',
    'instagram',
    'tiktok',
    'generic',
  ]);

export function resolveProvider(provider?: string): EmbedProvider {
  const normalized = (provider ?? '').trim().toLowerCase();
  if (normalized === 'x') return 'twitter';
  return KNOWN_PROVIDERS.has(normalized as EmbedProvider)
    ? (normalized as EmbedProvider)
    : 'generic';
}

/**
 * Normalizes a CSS aspect-ratio value ("16:9" or "16/9") into the CSS
 * `aspect-ratio` shorthand ("16 / 9"). Falls back when the value is missing
 * or malformed.
 */
export function toAspectRatio(value?: string, fallback = '16 / 9'): string {
  if (!value) return fallback;
  const parts = value.split(/[:/]/).map((p) => p.trim());
  if (parts.length === 2) {
    const [w, h] = parts.map(Number);
    if (Number.isFinite(w) && Number.isFinite(h) && w > 0 && h > 0) {
      return `${w} / ${h}`;
    }
  }
  return fallback;
}

const YOUTUBE_ID =
  /(?:youtube(?:-nocookie)?\.com\/(?:embed\/|watch\?v=)|youtu\.be\/)([\w-]{6,})/i;

/**
 * Allow-list of YouTube player parameters that are safe to carry through to the
 * reconstructed embed URL. We rebuild a canonical first-party URL from the video
 * id (so a crafted source can't inject an arbitrary origin), then re-attach only
 * these known display/playback params. Notably excludes `v`/`si` (not player
 * params) and `enablejsapi`/`origin`/`widget_referrer` (scripting surface).
 *
 * @see https://developers.google.com/youtube/player_parameters
 */
export const YOUTUBE_ALLOWED_PARAMS: ReadonlySet<string> = new Set([
  'cc_lang_pref',
  'cc_load_policy',
  'color',
  'controls',
  'disablekb',
  'end',
  'fs',
  'hl',
  'iv_load_policy',
  'list',
  'listType',
  'loop',
  'modestbranding',
  'playlist',
  'playsinline',
  'rel',
  'start',
]);

/**
 * Extracts the allow-listed YouTube player params from a source URL and returns
 * them as a query string (without the leading `?`). HTML-encoded ampersands
 * (`&amp;`) from WordPress content are decoded first. Returns an empty string
 * when there are no carryable params.
 */
function extractYouTubeParams(value: string): string {
  if (!value.includes('?')) return '';
  let search: URLSearchParams;
  try {
    search = new URL(value.replace(/&amp;/gi, '&'), 'https://x.invalid')
      .searchParams;
  } catch {
    return '';
  }
  const out = new URLSearchParams();
  search.forEach((val, key) => {
    if (YOUTUBE_ALLOWED_PARAMS.has(key) && /^[A-Za-z0-9_,-]+$/.test(val)) {
      out.append(key, val);
    }
  });
  return out.toString();
}

/**
 * Resolves a YouTube embed URL from a src/url/id. Accepts embed URLs, watch
 * URLs, short youtu.be links, or a bare video id. Approved player parameters
 * present on the source URL (e.g. `cc_load_policy`, `rel`, `start`) are carried
 * through to the canonical first-party embed URL.
 */
export function resolveYouTubeEmbedUrl(input?: string): string | null {
  if (!input) return null;
  const value = input.trim();
  const match = value.match(YOUTUBE_ID);
  const id = match ? match[1] : /^[\w-]{6,}$/.test(value) ? value : null;
  if (!id) return null;
  const params = extractYouTubeParams(value);
  const query = params ? `?${params}` : '';
  return `https://www.youtube-nocookie.com/embed/${id}${query}`;
}

const EVERVIZ_ID = /app\.everviz\.com\/(?:embed|inject)\/([\w-]+)/i;

/** Resolves an Everviz chart id from a src/url/id. */
export function resolveEvervizId(input?: string): string | null {
  if (!input) return null;
  const value = input.trim();
  const match = value.match(EVERVIZ_ID);
  if (match) return match[1];
  return /^[\w-]+$/.test(value) ? value : null;
}

export function evervizEmbedUrl(id: string): string {
  return `https://app.everviz.com/embed/${id}`;
}

const TIKTOK_ID = /tiktok\.com\/(?:embed\/(?:v\d+\/)?|@[^/]+\/video\/)(\d+)/i;

/** Resolves a TikTok video id from an id/url/src. */
export function resolveTikTokId(input?: string): string | null {
  if (!input) return null;
  const value = input.trim();
  if (/^\d+$/.test(value)) return value;
  return value.match(TIKTOK_ID)?.[1] ?? null;
}

export function tiktokEmbedUrl(id: string): string {
  return `https://www.tiktok.com/embed/v2/${id}`;
}

const TWITTER_STATUS = /status(?:es)?\/(\d+)/i;

/** Resolves an X/Twitter status id from a src/url/id. */
export function resolveTwitterId(input?: string): string | null {
  if (!input) return null;
  const value = input.trim();
  const match = value.match(TWITTER_STATUS);
  if (match) return match[1];
  return /^\d+$/.test(value) ? value : null;
}

/**
 * Guards the generic-fallback iframe `src` against non-http(s) schemes
 * (`javascript:`, `data:`, etc.) so an unknown-provider embed can't become an
 * injection vector. Accepts absolute http(s) and protocol-relative URLs.
 */
export function isSafeEmbedUrl(url?: string): boolean {
  if (!url) return false;
  return /^(https?:)?\/\//i.test(url.trim());
}

export const EXTERNAL_SCRIPTS = {
  instagram: 'https://www.instagram.com/embed.js',
  tiktok: 'https://www.tiktok.com/embed.js',
} as const;

/** Origin that Everviz embed iframes post their dimensions from. */
export const EVERVIZ_ORIGIN = 'https://app.everviz.com';
/** TikTok's native card width bounds (from its own blockquote styling). */
export const TIKTOK_MAX_WIDTH = 605;
export const TIKTOK_MIN_WIDTH = 325;

/**
 * Reads the content height from an Everviz `everviz-dimensions` resize message.
 * The embed iframe posts `{ type: 'everviz-dimensions', height, width, uuid }`
 * on load/resize so the host can size the frame to the chart (chrome + plot)
 * instead of clipping it inside a fixed aspect ratio.
 */
export function evervizFrameHeight(data: unknown): number | null {
  if (!data || typeof data !== 'object') return null;
  const payload = data as Record<string, unknown>;
  if (payload.type !== 'everviz-dimensions') return null;
  const height = Number(payload.height);
  return Number.isFinite(height) && height > 0 ? height : null;
}
